<template lang="pug">
yz-filter-menu(:label="'Item'" :is-active="isActive" :display-value="displayValue" :disable-apply="selectedItems.length === 0" :loading="loading" :max-height="500" :show-header="false" @update:menu="onMenuOpen" @clear="onClear" @apply="onApply")
  .menu-header.sticky.top-0.z-1
    .font-semibold.text-sm.pa-4 Filter by item
    yz-text-field.text-sm.mx-4.mb-1(v-model="searchText" :placeholder="$t('search')" size="lg" @input="onQueryChanged")
    .d-flex.align-center.justify-end.px-4.py-2(v-if="count > pageSize")
      yz-pagination(v-model="page" :page-size="pageSize" :count="count" :loading="loading" arrow, type="item")

    yz-divider

  .scrollable-content
    yz-skeleton-loader.pa-4(v-if="loading" type="text@10")

    div(v-else-if="error")
      .text-center.py-8
        .text-xs.neutral500--text.mb-3 {{ $t('error_message.error_occured') }}
        yz-btn(size="sm" variant="outlined" color="primary" @click="fetchItems") {{ $t('retry') }}

    div(v-else)
      template(v-if="items.length > 0")
        div(v-for="(item, index) in items" :key="item.id")
          .d-flex.text-sm.px-4.py-3.cursor-pointer.hover-bg(@click="onToggleItem(item.id)")
            yz-checkbox(:value="selectedItems.includes(item.id)" @click.native.prevent)
            .ml-4 {{ item.name }}
          yz-divider.my-0(v-if="index < items.length - 1")
      template(v-else)
        .text-center.py-8.text-xs.neutral500--text {{ $t('panel.no_items_found') }}
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters } from 'vuex'

export default {
  props: {
    filter: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      items: [],
      selectedItems: [],
      searchText: '',
      loading: false,
      isActive: false,
      displayValue: '',
      itemsLoaded: false,
      error: false,
      page: 1,
      pageSize: 10,
      count: 0
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    })
  },
  watch: {
    filter: {
      handler() {
        if (!this.filter.key) {
          this.selectedItems = []
          this.isActive = false
          this.displayValue = ''
        }
      }
    },
    page() {
      this.fetchItems()
    }
  },
  methods: {
    onMenuOpen(isOpen) {
      if (isOpen && !this.itemsLoaded) {
        this.fetchItems()
      }
    },
    onQueryChanged: debounce(function() {
      this.page = 1
      this.fetchItems()
    }, 750),
    async fetchItems() {
      this.loading = true
      this.error = false
      try {
        const params = {
          status: 'published',
          category: 'medication',
          page: this.page,
          page_size: this.pageSize
        }
        if (this.searchText) {
          params.search = this.searchText
        }
        const response = await this.$axios.get(`/clinics/${this.company.slug}/medications`, { params })
        this.items = (response.data.results || []).map(item => ({ id: item.id, name: item.name }))
        this.count = response.data.count || 0
        this.itemsLoaded = true
      } catch (error) {
        console.error('Error fetching items:', error)
        this.items = []
        this.error = true
      } finally {
        this.loading = false
      }
    },
    onToggleItem(itemId) {
      const index = this.selectedItems.indexOf(itemId)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      } else {
        this.selectedItems.push(itemId)
      }
    },
    onClear() {
      this.selectedItems = []
      this.searchText = ''
      this.isActive = false
      this.displayValue = ''
      this.$emit('update:filter', {})
    },
    onApply() {
      this.isActive = true
      const count = this.selectedItems.length

      if (count <= 2) {
        const labels = this.selectedItems.map(id => this.items.find(i => i.id === id)?.name)
        this.displayValue = labels.join(', ')
      } else {
        this.displayValue = `${count} ${this.$t('selected')}`
      }

      this.$emit('update:filter', {
        key: 'medications',
        value: this.selectedItems.join(',')
      })
    },
    reset() {
      this.onClear()
    },
    async restoreFromParams(params) {
      if (params.medications) {
        if (!this.itemsLoaded) {
          await this.fetchItems()
        }
        this.selectedItems = params.medications.split(',').map(id => parseInt(id))
        this.onApply()
      }
    }
  }
}
</script>

<style scoped>
.hover-bg:hover {
  background-color: #f5f5f5;
}

.menu-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  overflow: hidden;
}

.scrollable-content {
  max-height: 300px;
  overflow-y: auto;
}

.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #b0b0b0;
}
</style>
